import { useEffect, useState } from 'react'
import { NameSuggestion, SuggestionKind, api } from './api'

/**
 * Suggestions that make setting up a plan quicker (SM-21): categories offered
 * on every item, and names offered as you type ("Thames Water", "Child
 * Benefit") that fill in a category when picked.
 *
 * Both are only offers. A category is still free text and a name can be
 * anything, so a plan made before these existed, or one that ignores them,
 * works the same.
 */

/** Offered on every item's category, before anything the name list adds. */
export const DEFAULT_CATEGORIES = [
  'Housing',
  'Council Tax',
  'Utilities',
  'Phone & Internet',
  'Insurance',
  'Food',
  'Transport',
  'Household',
  'Debt',
  'Subscriptions',
  'Health',
  'Children',
  'Education',
  'Personal',
  'Pets',
  'Entertainment',
  'Gifts',
  'Holidays',
  'Charity',
  'Savings',
  'Income',
  'Benefits',
] as const

/**
 * The central list, fetched once per page load and shared by every form.
 * Kept when a fetch fails as an empty list: suggestions are a convenience,
 * never a reason a form can't open.
 */
let loaded: Promise<NameSuggestion[]> | null = null

function load(): Promise<NameSuggestion[]> {
  loaded ??= api.nameSuggestions().then(
    (r) => r.names,
    () => {
      loaded = null // try again next time a form opens
      return []
    },
  )
  return loaded
}

/** For tests: forget the list so the next form fetches it again. */
export function resetNameSuggestions() {
  loaded = null
}

export function useNameSuggestions(): NameSuggestion[] {
  const [names, setNames] = useState<NameSuggestion[]>([])
  useEffect(() => {
    let live = true
    void load().then((n) => {
      if (live) setNames(n)
    })
    return () => {
      live = false
    }
  }, [])
  return names
}

/** The names to offer for one kind of item. */
export function namesFor(all: NameSuggestion[], kind: SuggestionKind): NameSuggestion[] {
  return all.filter((s) => s.kind === kind)
}

/** The suggestion a typed name is, ignoring case and spacing — how picking one from the list is noticed. */
export function suggestionNamed(all: NameSuggestion[], kind: SuggestionKind, name: string): NameSuggestion | undefined {
  const wanted = name.trim().replace(/\s+/g, ' ').toLowerCase()
  if (wanted === '') return undefined
  return all.find((s) => s.kind === kind && s.name.toLowerCase() === wanted)
}

/** Categories to offer: the defaults and any others the name list uses, alphabetical, without repeats. */
export function categoryOptions(all: NameSuggestion[]): string[] {
  const seen = new Set<string>(DEFAULT_CATEGORIES.map((c) => c.toLowerCase()))
  const extra: string[] = []
  for (const s of all) {
    if (s.category && !seen.has(s.category.toLowerCase())) {
      seen.add(s.category.toLowerCase())
      extra.push(s.category)
    }
  }
  return [...DEFAULT_CATEGORIES, ...extra].sort((a, b) => a.localeCompare(b, 'en-GB', { sensitivity: 'base' }))
}

/**
 * The category to show after the name changes. Picking a suggestion fills in
 * its category, but only over an empty one or one an earlier pick filled in —
 * never over a category the person typed themselves.
 */
export function categoryAfterNameChange(
  all: NameSuggestion[],
  kind: SuggestionKind,
  name: string,
  category: string,
  filledIn: string | null,
): { category: string; filledIn: string | null } {
  const picked = suggestionNamed(all, kind, name)
  const mayReplace = category === '' || category === filledIn
  if (picked?.category && mayReplace) return { category: picked.category, filledIn: picked.category }
  return { category, filledIn }
}
